import { ColDef } from "@ag-grid-community/core";
import { createSelector } from "reselect";

import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import { RootState } from "@app/store/rootReducer";

import GROUP_COLUMN_DEF from "./groupColumnDef";

const activityState = (state: RootState) => state.scene.activity;

export const getGridColumnDefs = createSelector(
  [activityState, (_, forClientSideGrid?: boolean) => forClientSideGrid],
  ({ columns }, forClientSideGrid) => {
    // get field's gridProperties && also add width/pinned/hide props from the column model
    const columnDefs = columns
      .filter(({ field }) => field.isReadable || field.isSystemRequired)
      .map<ColDef>(({ field, pinned, visible, width }) => ({
        ...(field.gridProperties(forClientSideGrid) ?? {}),
        hide: !visible,
        pinned,
        width,
      }));

    // always make a checkbox/group column to appear on the left side of a grid
    columnDefs.unshift(GROUP_COLUMN_DEF);

    return columnDefs;
  }
);

export const getListViewState = createSelector(
  activityState,
  ({ columns, filter, range, selectedSavedFilterId, sort }): ViewState => ({
    columns,
    filter,
    range,
    selectedSavedFilterId,
    sort,
  })
);

export const getLoading = createSelector(activityState, ({ loading }) => loading);

export const getUpdateActivityLoadingIds = createSelector(
  activityState,
  ({ updateActivityLoading }) => updateActivityLoading
);

export const getBulkPostponeLoading = createSelector(
  activityState,
  ({ bulkPostponeLoading }) => bulkPostponeLoading
);

export const getToggleCompleteLoadingIds = createSelector(
  activityState,
  ({ toggleCompleteLoading }) => toggleCompleteLoading
);

export const getBulkToggleCompleteLoading = createSelector(
  activityState,
  ({ bulkToggleCompleteLoading }) => bulkToggleCompleteLoading
);

export const getDeleteLoadingIds = createSelector(
  activityState,
  ({ deleteLoading }) => deleteLoading
);

export const getBulkDeleteLoading = createSelector(
  activityState,
  ({ bulkDeleteLoading }) => bulkDeleteLoading
);

export const getUpdateNoteLoadingIds = createSelector(
  activityState,
  ({ updateNoteLoading }) => updateNoteLoading
);

export const getUploadedFiles = createSelector(activityState, ({ filesAdded }) => filesAdded);

export const getFilePreview = createSelector(activityState, ({ filePreview }) => filePreview);
export const getFilePreviewId = createSelector(activityState, ({ filePreviewId }) => filePreviewId);

export const isFilePreviewLoading = createSelector(
  activityState,
  ({ filePreviewLoading }) => filePreviewLoading
);

export const getActivityAssignee = createSelector(
  activityState,
  ({ activityAssignee }) => activityAssignee
);

export const getActivityVisibilityTeamIds = createSelector(
  activityState,
  ({ activityVisibilityTeamIds }) => activityVisibilityTeamIds
);

export const getActivityType = createSelector(activityState, ({ activityType }) => activityType);

export const getBulkEditLoading = createSelector(
  activityState,
  ({ bulkEditLoading }) => bulkEditLoading
);

export const getActivityVisibility = createSelector(
  activityState,
  ({ activityVisibility }) => activityVisibility
);

export const getTotalFilteredRecordsCount = createSelector(
  activityState,
  ({ totalFilteredRecords }) => totalFilteredRecords
);

export const getTotalRecordsCount = createSelector(
  activityState,
  ({ totalRecords }) => totalRecords
);

export const getUpdateViewStateCallback = createSelector(
  activityState,
  ({ onViewStateChanged }) => onViewStateChanged
);
